/**
 * Risk Assessment Service
 * 
 * Handles all risk assessment-related API calls and type definitions
 */

import { apiRequest } from './apiClient';
import { EmptyResponse, RiskRanking, SyncfusionSearchParam } from './types';
import { parseCommaSeparated } from '../utils/stringUtils';

/**
 * Backend API Response Types
 */
export interface RiskAssessmentProcessBackend {
  assessmentId: string;
  cycleName: string;
  processName: string;
  processOwnerName: string;
  overallRiskRanking: 'unranked' | 'low' | 'med' | 'high';
  fraudRiskRanking: 'unranked' | 'low' | 'med' | 'high';
  objectiveCount: number;
  fraudRiskCount: number;
}

export interface GetRiskAssessmentProcessesResponse {
  data: RiskAssessmentProcessBackend[];
  code?: string;
  message?: string;
  exceptionName?: string;
  stackTrace?: string;
}

/**
 * Frontend Types
 */
export interface RiskAssessment {
  id: string;
  cycleCode: string;
  cycleName: string;
  processCode: string;
  processName: string;
  riskRanking: RiskRanking;
  fraudRiskRank: RiskRanking;
  objectivesCount: number;
  fraudRisksCount: number;
  processOwnerName?: string;
}

export interface Cycle {
  id: string;
  cycleCode: string;
  cycleName: string;
  bgColor: string;
  riskAssessments: RiskAssessment[];
}

/**
 * API Functions
 */

/**
 * Get all risk assessment processes for an annual report
 * @param annualReportId - Annual report ID
 * @returns Array of risk assessment processes grouped by cycle
 */
export const getRiskAssessmentProcesses = async (
  annualReportId: string
): Promise<GetRiskAssessmentProcessesResponse> => {
  try {
    return await apiRequest<GetRiskAssessmentProcessesResponse, never>(
      'GET',
      `/risk-assessment/processes/${annualReportId}`,
      undefined,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to fetch risk assessment processes',
    };
  }
};

/**
 * Get risk assessment by process ID
 * @param processId - Process ID
 * @returns Risk assessment data for the process
 */
export const getRiskAssessmentByProcessId = async (
  processId: string
): Promise<GetRiskAssessmentProcessesResponse> => {
  try {
    return await apiRequest<GetRiskAssessmentProcessesResponse, never>(
      'GET',
      `/risk-assessment/${processId}`,
      undefined,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to fetch risk assessment',
    };
  }
};

/**
 * Assessment Details Types
 */
export interface AssessmentDetailsBackend {
  assessmentId: string; // Assuming this is camelCase from response
  quantitativeAssessment: string;
  overallRiskRanking: RiskRanking | null;
  overallRiskDescription: string;
  incentives: string;
  opportunities: string;
  rationalizations: string;
  fraudRiskRanking: string | null;
  fraudRiskDescription: string;
}

export interface GetAssessmentDetailsResponse {
  data: AssessmentDetailsBackend;
  code?: string;
  message?: string;
  exceptionName?: string;
  stackTrace?: string;
}

/**
 * Get assessment details by assessment ID
 * @param assessmentId - Assessment ID
 * @returns Assessment details including quantitative text and fraud risk data
 */
export const getAssessmentDetails = async (
  assessmentId: string
): Promise<GetAssessmentDetailsResponse> => {
  try {
    return await apiRequest<GetAssessmentDetailsResponse, never>(
      'GET',
      `/risk-assessment/details/${assessmentId}`,
      undefined,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to fetch assessment details',
    };
  }
};

/**
 * Qualitative Factor Types
 */
export interface QualitativeFactorBackend {
  Id: string;
  QualitativeFactor: string;
  RiskRanking: string | null;
  Comment: string;
  Index: number;
  CreatedAt?: string;
}

export interface GetQualitativeFactorsRequest {
  skip: number;
  take: number;
  requiresCounts: boolean;
  search?: SyncfusionSearchParam[];
}

export interface GetQualitativeFactorsResponse {
  result: QualitativeFactorBackend[];
  count: number;
  code?: string;
  message?: string;
  exceptionName?: string;
  stackTrace?: string;
}

/**
 * Get qualitative factors for an assessment
 * @param assessmentId - Assessment ID
 * @param request - Pagination and filtering parameters
 * @returns List of qualitative factors
 */
export const getQualitativeFactors = async (
  assessmentId: string,
  request: GetQualitativeFactorsRequest
): Promise<GetQualitativeFactorsResponse> => {
  try {
    const requestBody = {
      skip: request.skip,
      take: request.take,
      requiresCounts: request.requiresCounts,
      ...(request.search && { search: request.search }),
    };
    
    return await apiRequest<GetQualitativeFactorsResponse, typeof requestBody>(
      'POST',
      `/risk-assessment/qualitative/index-grid/${assessmentId}`,
      requestBody,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to fetch qualitative factors',
    };
  }
};

/**
 * Create Qualitative Factor Request/Response Types
 */
export interface CreateQualitativeFactorRequest {
  assessmentId: string;
  index: number;
  qualitativeFactor: string;
  riskRating: string;
  comment: string;
}


/**
 * Create a new qualitative factor
 * @param request - Qualitative factor data
 * @returns Response from API
 */
export const createQualitativeFactor = async (
  request: CreateQualitativeFactorRequest
): Promise<EmptyResponse> => {
  try {
    return await apiRequest<EmptyResponse, CreateQualitativeFactorRequest>(
      'POST',
      '/risk-assessment/qualitative',
      request,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to create qualitative factor',
    };
  }
};

/**
 * Update Qualitative Factor Request/Response Types
 */
export interface UpdateQualitativeFactorRequest {
  assessmentId: string;
  index: number;
  qualitativeFactor: string;
  riskRating: string;
  comment: string;
  assessmentItemId: string; // Note: Backend naming, should be qualitativeFactorId
}

/**
 * Update an existing qualitative factor
 * @param request - Updated qualitative factor data
 * @returns Response from API
 */
export const updateQualitativeFactor = async (
  request: UpdateQualitativeFactorRequest
): Promise<EmptyResponse> => {
  try {
    return await apiRequest<EmptyResponse, UpdateQualitativeFactorRequest>(
      'PUT',
      '/risk-assessment/qualitative',
      request,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to update qualitative factor',
    };
  }
};

/**
 * Delete Qualitative Factor Response Type
 */
export interface DeleteQualitativeFactorResponse {
  data?: any;
  code?: string;
  message?: string;
  exceptionName?: string;
  stackTrace?: string;
}

/**
 * Delete a qualitative factor
 * @param qualitativeFactorId - ID of the qualitative factor to delete (assessmentItemId from backend)
 * @returns Response from API
 */
export const deleteQualitativeFactor = async (
  qualitativeFactorId: string
): Promise<DeleteQualitativeFactorResponse> => {
  try {
    return await apiRequest<DeleteQualitativeFactorResponse, never>(
      'DELETE',
      `/risk-assessment/qualitative/${qualitativeFactorId}`,
      undefined,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to delete qualitative factor',
    };
  }
};

/**
 * Update Quantitative Assessment Request/Response Types
 */
export interface UpdateQuantitativeRequest {
  assessmentId: string;
  content: string;
}

export interface UpdateQuantitativeResponse {
  data?: any;
  code?: string;
  message?: string;
  exceptionName?: string;
  stackTrace?: string;
}

/**
 * Update quantitative assessment text
 * @param request - Quantitative assessment data
 * @returns Response from API
 */
export const updateQuantitativeAssessment = async (
  request: UpdateQuantitativeRequest
): Promise<UpdateQuantitativeResponse> => {
  try {
    return await apiRequest<UpdateQuantitativeResponse, UpdateQuantitativeRequest>(
      'PUT',
      '/risk-assessment/quantitative',
      request,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to update quantitative assessment',
    };
  }
};

/**
 * Update Fraud Risk Request/Response Types
 */
export interface UpdateFraudRiskRequest {
  assessmentId: string;
  riskRanking: string;
  description: string;
}

/**
 * Update fraud risk ranking and description
 * @param request - Fraud risk data
 * @returns Response from API
 */
export const updateFraudRisk = async (
  request: UpdateFraudRiskRequest
): Promise<EmptyResponse> => {
  try {
    return await apiRequest<EmptyResponse, UpdateFraudRiskRequest>(
      'POST',
      '/risk-assessment/fraud',
      request,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to update fraud risk',
    };
  }
};

/**
 * Update Overall Risk Request/Response Types
 */
export interface UpdateOverallRiskRequest {
  assessmentId: string;
  riskRanking: string;
  description: string;
}

/**
 * Update overall risk ranking and description
 * @param request - Overall risk data
 * @returns Response from API
 */
export const updateOverallRisk = async (
  request: UpdateOverallRiskRequest
): Promise<EmptyResponse> => {
  try {
    return await apiRequest<EmptyResponse, UpdateOverallRiskRequest>(
      'POST',
      '/risk-assessment/overall',
      request,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to update overall risk',
    };
  }
};

/**
 * Fraud Triangle Card Update Types (Tab 3)
 */
export interface UpdateFraudTriangleCardRequest {
  assessmentId: string;
  content: string;
}

/**
 * Update Incentives & Pressures card (Tab 3)
 * @param request - Incentives content
 * @returns Response from API
 */
export const updateIncentives = async (
  request: UpdateFraudTriangleCardRequest
): Promise<EmptyResponse> => {
  try {
    return await apiRequest<EmptyResponse, UpdateFraudTriangleCardRequest>(
      'PUT',
      '/risk-assessment/incentives',
      request,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to update incentives',
    };
  }
};

/**
 * Update Opportunities card (Tab 3)
 * @param request - Opportunities content
 * @returns Response from API
 */
export const updateOpportunities = async (
  request: UpdateFraudTriangleCardRequest
): Promise<EmptyResponse> => {
  try {
    return await apiRequest<EmptyResponse, UpdateFraudTriangleCardRequest>(
      'PUT',
      '/risk-assessment/opportunities',
      request,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to update opportunities',
    };
  }
};

/**
 * Update Rationalizations & Attitude card (Tab 3)
 * @param request - Rationalizations content
 * @returns Response from API
 */
export const updateRationalizations = async (
  request: UpdateFraudTriangleCardRequest
): Promise<EmptyResponse> => {
  try {
    return await apiRequest<EmptyResponse, UpdateFraudTriangleCardRequest>(
      'PUT',
      '/risk-assessment/rationalizations',
      request,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to update rationalizations',
    };
  }
};

/**
 * Assertions Types and APIs
 */

export interface AssertionBackend {
  assertionId: string;
  assertionName: string;
  createdAt: string;
}

export interface GetAssertionsResponse {
  data: AssertionBackend[];
  code?: string;
  message?: string;
  exceptionName?: string;
  stackTrace?: string;
}

export interface GetAssertionDetailsResponse {
  data: AssertionBackend;
  code?: string;
  message?: string;
  exceptionName?: string;
  stackTrace?: string;
}

export interface CreateAssertionRequest {
  assertionName: string;
}

export interface UpdateAssertionRequest {
  assertionId: string;
  assertionName: string;
}

/**
 * Frontend Assertion Type
 */
export interface Assertion {
  id: string;
  name: string;
  createdAt: string;
}

/**
 * Get all assertions
 * @returns List of assertions
 */
export const getAssertions = async (): Promise<GetAssertionsResponse> => {
  try {
    return await apiRequest<GetAssertionsResponse, never>(
      'GET',
      '/risk-assessment/assertions',
      undefined,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to fetch assertions',
    };
  }
};

/**
 * Parse assertions from backend format
 */
export const parseAssertions = (backendData: AssertionBackend[]): Assertion[] => {
  if (!backendData || !Array.isArray(backendData)) {
    console.warn('⚠️ parseAssertions received invalid data:', backendData);
    return [];
  }

  return backendData.map(assertion => ({
    id: assertion.assertionId,
    name: assertion.assertionName,
    createdAt: assertion.createdAt
  }));
};

/**
 * Create new assertion
 * @returns List of assertions
 */
export const createAssertion = async (request: CreateAssertionRequest): Promise<GetAssertionDetailsResponse> => {
  try {
    return await apiRequest<GetAssertionDetailsResponse, CreateAssertionRequest>(
      'POST',
      '/admin/assertion',
      request,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to create assertion',
    };
  }
};

/**
 * Update existing assertion name
 * @returns List of assertions
 */
export const updateAssertion = async (request: UpdateAssertionRequest): Promise<GetAssertionDetailsResponse> => {
  try {
    return await apiRequest<GetAssertionDetailsResponse, UpdateAssertionRequest>(
      'PUT',
      '/admin/assertion',
      request,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to create assertion',
    };
  }
};

/**
 * Update existing assertion name
 * @returns List of assertions
 */
export const deleteAssertion = async (assertionId: string): Promise<GetAssertionDetailsResponse> => {
  try {
    return await apiRequest<GetAssertionDetailsResponse, never>(
      'DELETE',
      '/admin/assertion/' + assertionId,
      undefined,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to create assertion',
    };
  }
};

/**
 * Objectives (Tab 2) Types and APIs
 */

export interface ObjectiveBackend {
  Id: string;
  Number: string;
  ObjectiveName: string;
  RiskRatingSeverityIndex: number | null;
  RiskRatingLikelihoodIndex: number | null;
  Risks: string;
  ControlIds: string;
  ControlCodes: string;
  ControlRiskRanking: string | null;
  ControlText: string | null;
  AssertionIds: string;
  AssertionNames: string;
  CreatedAt?: string;
}

export interface GetObjectivesRequest {
  skip: number;
  take: number;
  requiresCounts: boolean;
  search?: SyncfusionSearchParam[];
}

export interface GetObjectivesResponse {
  result: ObjectiveBackend[];
  count: number;
}

export interface CreateObjectiveRequest {
  assessmentId: string;
  number: number;
  objectives: string;
  risks: string;
  riskRating: string;
  riskRatingSeverityIndex: number | null;
  riskRatingLikelihoodIndex: number | null;
  controlIds: string[];
  controlText?: string | null;
  assertionIds: string[];
}

export interface UpdateObjectiveRequest {
  assessmentId: string;
  objectives: string;
  risks: string;
  riskRating: string;
  riskRatingSeverityIndex: number | null;
  riskRatingLikelihoodIndex: number | null;
  controlIds: string[];
  controlText?: string | null;
  assertionIds: string[];
  objectiveId: string;
}

/**
 * Frontend Objective Type (used in Tab2ObjectivesAndRisks)
 */
export interface Objective {
  id: string;
  index: number; // For display (1, 2, 3...)
  indexStr: string;
  objectives: string;
  risks: string;
  controlRiskRanking: RiskRanking;
  financialStatementAssertions: string[]; // ['Validity', 'Completeness', etc.]
  financialStatementAssertionIds: string[]; // Assertion IDs for API
  riskMitigatingControls: string[]; // ['A01.03', 'A01.10'] - Control codes for display
  controlActivityIds: string[]; // Control activity IDs for linking
  controlText: string; // Free text for manual control input
  controlInputMode?: 'link' | 'manual'; // For drawer
  riskRatingSeverityIndex: number | null;
  riskRatingLikelihoodIndex: number | null;
}

export interface ObjectiveResponse {
  data?: any;
  code?: string;
  message?: string;
  exceptionName?: string;
  stackTrace?: string;
}

/**
 * Fetch objectives list for an assessment
 * @param assessmentId - Assessment ID
 * @param request - Pagination and filter options
 * @returns List of objectives
 */
export const getObjectives = async (
  assessmentId: string,
  request: GetObjectivesRequest
): Promise<GetObjectivesResponse> => {
  try {
    const requestBody = {
      skip: request.skip,
      take: request.take,
      requiresCounts: request.requiresCounts,
      ...(request.search && { search: request.search }),
    };
    
    return await apiRequest<GetObjectivesResponse, typeof requestBody>(
      'POST',
      `/risk-assessment/objectives/index-grid/${assessmentId}`,
      requestBody,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to fetch objectives',
    };
  }
};

/**
 * Create a new objective
 * @param request - Objective data
 * @returns Response from API
 */
export const createObjective = async (
  request: CreateObjectiveRequest
): Promise<ObjectiveResponse> => {
  try {
    return await apiRequest<ObjectiveResponse, CreateObjectiveRequest>(
      'POST',
      '/risk-assessment/objective',
      request,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to create objective',
    };
  }
};

/**
 * Update an existing objective
 * @param request - Updated objective data
 * @returns Response from API
 */
export const updateObjective = async (
  request: UpdateObjectiveRequest
): Promise<ObjectiveResponse> => {
  try {
    return await apiRequest<ObjectiveResponse, UpdateObjectiveRequest>(
      'PUT',
      '/risk-assessment/objective',
      request,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to update objective',
    };
  }
};

/**
 * Delete an objective
 * @param objectiveId - ID of the objective to delete
 * @returns Response from API
 */
export const deleteObjective = async (
  objectiveId: string
): Promise<ObjectiveResponse> => {
  try {
    return await apiRequest<ObjectiveResponse, never>(
      'DELETE',
      `/risk-assessment/objective/${objectiveId}`,
      undefined,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to delete objective',
    };
  }
};

/**
 * Parse objectives from backend format
 * Sorts by Number field (0-based from backend)
 */
export const parseObjectives = (
  backendData: ObjectiveBackend[]
): Objective[] => {
  if (!backendData || !Array.isArray(backendData)) {
    console.warn('⚠️ parseObjectives received invalid data:', backendData);
    return [];
  }

  return backendData
    .map(objective => {
      // Parse control activity IDs (for API)
      const controlIds = parseCommaSeparated(objective.ControlIds || '');

      // Parse control codes (for display)
      const controlCodes = parseCommaSeparated(objective.ControlCodes || '');
      
      // Parse assertion names (for display)
      const assertionNames = parseCommaSeparated(objective.AssertionNames || '');

      // Parse assertion IDs (for API)
      const assertionIds = parseCommaSeparated(objective.AssertionIds || '');

      // Get control text (for manual input)
      const controlText = objective.ControlText || '';

      // Determine control input mode based on controlText
      const hasControlText = controlText.trim() !== '';
      const controlInputMode: 'link' | 'manual' = hasControlText ? 'manual' : 'link';

      return {
        id: objective.Id,
        index: parseInt(objective.Number), // Convert 0-based to 1-based for display
        indexStr: objective.Number,
        objectives: objective.ObjectiveName || '',
        risks: objective.Risks || '',
        controlRiskRanking: normalizeRiskRanking(objective.ControlRiskRanking),
        financialStatementAssertions: assertionNames,
        financialStatementAssertionIds: assertionIds,
        riskMitigatingControls: controlCodes,
        controlActivityIds: controlIds,
        controlText: controlText,
        controlInputMode: controlInputMode,
        riskRatingSeverityIndex: objective.RiskRatingSeverityIndex || null,
        riskRatingLikelihoodIndex: objective.RiskRatingLikelihoodIndex || null,
      };
    })
    .sort((a, b) => a.index - b.index);
};

/**
 * Helper Functions
 */

// Material color palette for cycles (same as hierarchyService)
const CYCLE_COLORS = [
  '#FEF3C7', // Yellow 50
  '#DBEAFE', // Blue 50
  '#D1FAE5', // Green 50
  '#FCE7F3', // Pink 50
  '#E0F2F1', // Teal 50
  '#F3E5F5', // Purple 50
  '#FFF3E0', // Orange 50
  '#F1F8E9', // Light Green 50
  '#E1F5FE', // Light Blue 50
  '#FBE9E7', // Deep Orange 50
];

/**
 * Normalize risk ranking from backend format to frontend format
 */
export const normalizeRiskRanking = (ranking: string | null | undefined): RiskRanking => {
  if (!ranking) {
    return 'Unranked';
  }
  
  const normalized = ranking.toLowerCase();
  switch (normalized) {
    case 'high':
      return 'High';
    case 'med':
    case 'medium':
      return 'Med';
    case 'low':
      return 'Low';
    case 'unranked':
    default:
      return 'Unranked';
  }
};

/**
 * Convert frontend risk ranking to backend format
 */
export const denormalizeRiskRanking = (
  ranking: RiskRanking
): string => {
  switch (ranking) {
    case 'High':
      return 'high';
    case 'Med':
      return 'med';
    case 'Low':
      return 'low';
    case 'Unranked':
    default:
      return 'unranked';
  }
};

/**
 * Compliance Purpose (Tab 3) Types and APIs
 */

export interface CompliancePurposeBackend {
  Id: string;
  Number: string;
  RiskFactors: string; // Description/Specific Fraud Risk Factors
  ObjectiveIds: string;
  ObjectiveNumbers: string; // Tilde-separated objective numbers for display
  ObjectiveProcessCodes: string; // Tilde-separated process codes (parallel to ObjectiveNumbers)
  ControlIds: string;
  ControlCodes: string; // Comma-separated control codes for display
  CreatedAt?: string;
}

export interface GetCompliancePurposesRequest {
  skip: number;
  take: number;
  requiresCounts: boolean;
  search?: SyncfusionSearchParam[];
}
  
export interface GetCompliancePurposesResponse {
  result: CompliancePurposeBackend[];
  count: number;
  code?: string;
  message?: string;
  exceptionName?: string;
  stackTrace?: string;
}

export interface CreateCompliancePurposeRequest {
  assessmentId: string;
  number: number;
  description: string;
  objectiveIds: string[];
  controlIds: string[];
}

export interface UpdateCompliancePurposeRequest {
  assessmentId: string;
  description: string;
  objectiveIds: string[];
  controlIds: string[];
  compliancePurposeId: string;
}

export interface CompliancePurposeResponse {
  data?: any;
  code?: string;
  message?: string;
  exceptionName?: string;
  stackTrace?: string;
}

/**
 * Frontend Compliance Purpose Type (used in Tab3FraudRisk)
 */
export interface CompliancePurpose {
  id: string;
  index: number; // For display (1, 2, 3...)
  indexStr: string;
  specificFraudRiskFactors: string; // Description field
  objectives: string[]; // Objective IDs for picker
  objectiveNumbers: string[]; // Objective numbers for display (e.g., ['01', '02'])
  objectiveProcessCodes: string[]; // Process codes parallel to objectiveNumbers (e.g., ['A01', 'A02'])
  fraudRiskMitigatingControls: string[]; // Control IDs for picker
  controlCodes: string[]; // Control codes for display (e.g., ['A01.03'])
}

/**
 * Fetch compliance purposes list for an assessment
 * @param assessmentId - Assessment ID
 * @param request - Pagination and filter options
 * @returns List of compliance purposes
 */
export const getCompliancePurposes = async (
  assessmentId: string,
  request: GetCompliancePurposesRequest
): Promise<GetCompliancePurposesResponse> => {
  try {
    const requestBody = {
      skip: request.skip,
      take: request.take,
      requiresCounts: request.requiresCounts,
      ...(request.search && { search: request.search }),
    };
    
    return await apiRequest<GetCompliancePurposesResponse, typeof requestBody>(
      'POST',
      `/risk-assessment/compliance/index-grid/${assessmentId}`,
      requestBody,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to fetch compliance purposes',
    };
  }
};

/**
 * Create a new compliance purpose
 * @param request - Compliance purpose data
 * @returns Response from API
 */
export const createCompliancePurpose = async (
  request: CreateCompliancePurposeRequest
): Promise<CompliancePurposeResponse> => {
  try {
    return await apiRequest<CompliancePurposeResponse, CreateCompliancePurposeRequest>(
      'POST',
      '/risk-assessment/compliance',
      request,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to create compliance purpose',
    };
  }
};

/**
 * Update an existing compliance purpose
 * @param request - Updated compliance purpose data
 * @returns Response from API
 */
export const updateCompliancePurpose = async (
  request: UpdateCompliancePurposeRequest
): Promise<CompliancePurposeResponse> => {
  try {
    return await apiRequest<CompliancePurposeResponse, UpdateCompliancePurposeRequest>(
      'PUT',
      '/risk-assessment/compliance',
      request,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to update compliance purpose',
    };
  }
};

/**
 * Delete a compliance purpose
 * @param compliancePurposeId - ID of the compliance purpose to delete
 * @returns Response from API
 */
export const deleteCompliancePurpose = async (
  compliancePurposeId: string
): Promise<CompliancePurposeResponse> => {
  try {
    return await apiRequest<CompliancePurposeResponse, never>(
      'DELETE',
      `/risk-assessment/compliance/${compliancePurposeId}`,
      undefined,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to delete compliance purpose',
    };
  }
};

/**
 * Parse compliance purposes from backend format
 * Sorts by Number field (0-based from backend)
 */
export const parseCompliancePurposes = (
  backendData: CompliancePurposeBackend[]
): CompliancePurpose[] => {
  if (!backendData || !Array.isArray(backendData)) {
    console.warn('⚠️ parseCompliancePurposes received invalid data:', backendData);
    return [];
  }

  return backendData
    .map(item => {
      // Parse objective IDs
      const objectiveIds = parseCommaSeparated(item.ObjectiveIds || '');

      // Parse objective numbers for display
      const objectiveNumbers = parseCommaSeparated(item.ObjectiveNumbers || '');

      // Parse objective process codes (parallel array to objectiveNumbers)
      const objectiveProcessCodes = parseCommaSeparated(item.ObjectiveProcessCodes || '');

      // Parse control IDs
      const controlIds = parseCommaSeparated(item.ControlIds || '');

      // Parse control codes for display
      const controlCodes = parseCommaSeparated(item.ControlCodes || '');

      return {
        id: item.Id,
        index: parseInt(item.Number), // Convert 0-based to 1-based for display
        indexStr: item.Number,
        specificFraudRiskFactors: item.RiskFactors || '',
        objectives: objectiveIds,
        objectiveNumbers: objectiveNumbers,
        objectiveProcessCodes: objectiveProcessCodes,
        fraudRiskMitigatingControls: controlIds,
        controlCodes: controlCodes,
      };
    })
    .sort((a, b) => a.index - b.index);
};

/**
 * Parse qualitative factors from backend format
 * Sorts by Index
 */
export const parseQualitativeFactors = (
  backendData: QualitativeFactorBackend[]
): Array<{
  id: string;
  qualitativeFactor: string;
  riskRanking: RiskRanking;
  customComment: string;
  displayOrder: number;
}> => {
  if (!backendData || !Array.isArray(backendData)) {
    console.warn('⚠️ parseQualitativeFactors received invalid data:', backendData);
    return [];
  }

  return backendData
    .map(factor => ({
      id: factor.Id || `qf-${Date.now()}-${Math.random()}`,
      qualitativeFactor: factor.QualitativeFactor || '',
      riskRanking: normalizeRiskRanking(factor.RiskRanking),
      customComment: factor.Comment || '',
      displayOrder: factor.Index ?? 0,
    }))
    .sort((a, b) => a.displayOrder - b.displayOrder);
};

/**
 * Extract code and name from backend string
 * E.g. "A00 Cycle Test 1" -> { code: "A00", name: "Cycle Test 1" }
 */
const extractCodeAndName = (fullString: string): { code: string; name: string } => {
  if (!fullString) {
    return { code: '', name: '' };
  }

  // Match pattern: "A00 Rest of the name" or "A01 Rest of the name"
  const match = fullString.match(/^([A-Z]\d{2})\s+(.+)$/);
  
  if (match) {
    return {
      code: match[1],      // "A00" or "A01"
      name: match[2],      // "Cycle Test 1" or "Process Name"
    };
  }

  // Fallback: no code found, return full string as name
  return {
    code: '',
    name: fullString,
  };
};

/**
 * Parse and group risk assessment processes by cycle
 * Extracts cycle code and process code from backend strings
 * Sorts cycles alphabetically by code (A00, B00, C00, etc.)
 */
export const parseAndGroupRiskAssessments = (
  backendData: RiskAssessmentProcessBackend[]
): Cycle[] => {
  // Group by cycle name (full string)
  const cycleMap = new Map<string, RiskAssessmentProcessBackend[]>();
  
  backendData.forEach(item => {
    const cycleName = item.cycleName || 'Uncategorized';
    if (!cycleMap.has(cycleName)) {
      cycleMap.set(cycleName, []);
    }
    cycleMap.get(cycleName)!.push(item);
  });

  // Convert to Cycle array with extracted codes
  const cycles: Cycle[] = [];

  cycleMap.forEach((processes, cycleFullName) => {
    // Extract cycle code and name
    const { code: cycleCode, name: cycleName } = extractCodeAndName(cycleFullName);
    
    // Determine background color based on cycle code
    const cycleIndex = cycleCode.charCodeAt(0) - 65; // A=0, B=1, C=2, etc.
    const bgColor = CYCLE_COLORS[cycleIndex % CYCLE_COLORS.length];

    // Sort processes by process code within the cycle
    const sortedProcesses = [...processes].sort((a, b) => {
      const codeA = extractCodeAndName(a.processName).code;
      const codeB = extractCodeAndName(b.processName).code;
      return codeA.localeCompare(codeB);
    });

    const riskAssessments: RiskAssessment[] = sortedProcesses.map(process => {
      // Extract process code and name
      const { code: processCode, name: processName } = extractCodeAndName(process.processName);
      
      return {
        id: process.assessmentId,
        cycleCode: cycleCode || cycleFullName,
        cycleName: cycleName || cycleFullName,
        processCode: processCode || process.processName,
        processName: processName || process.processName,
        riskRanking: normalizeRiskRanking(process.overallRiskRanking),
        fraudRiskRank: normalizeRiskRanking(process.fraudRiskRanking),
        objectivesCount: process.objectiveCount,
        fraudRisksCount: process.fraudRiskCount,
        processOwnerName: process.processOwnerName,
      };
    });

    cycles.push({
      id: `cycle-${cycleCode || cycleIndex}`,
      cycleCode: cycleCode || cycleFullName,
      cycleName: cycleName || cycleFullName,
      bgColor,
      riskAssessments,
    });
  });

  // Sort cycles alphabetically by cycle code (A00, B00, C00, etc.)
  cycles.sort((a, b) => a.cycleCode.localeCompare(b.cycleCode));

  return cycles;
};

/**
 * Rubric Item Types and APIs (Tab 4 - Risk Matrix Configuration)
 */

export interface RubricItemBackend {
  Id: string;
  RubricItemTitle: string;
  RubricItemDescription: string;
  Number: number;
  CreatedAt?: string;
}

export interface GetRubricItemsRequest {
  skip: number;
  take: number;
}
export interface UpdateRubricItemRequest {
  rubricItemId: string;
  rubricItemDescription: string;
}

export interface UpdateRubricItemResponse {
  data?: any;
  code?: string;
  message?: string;
  exceptionName?: string;
  stackTrace?: string;
}

/**
 * Frontend Rubric Item Type
 */
export interface RubricItem {
  id: string;
  name: string;
  description: string;
  index: number;
}

/**
 * Fetch likelihood rubric items for an assessment
 * @param annualReportId - Annual Report ID
 * @param request - Pagination parameters
 * @returns List of likelihood rubric items
 */
export const getLikelihoodRubricItems = async (
  annualReportId: string,
  request: GetRubricItemsRequest
): Promise<RubricItemBackend[]> => {
  try {
    return await apiRequest<RubricItemBackend[], GetRubricItemsRequest>(
      'POST',
      `/risk-assessment/rubric/likelyhood/index-grid/${annualReportId}`,
      request,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to fetch likelihood rubric items',
    };
  }
};

/**
 * Fetch severity rubric items for an assessment
 * @param annualReportId - Annual Report ID
 * @param request - Pagination parameters
 * @returns List of severity rubric items
 */
export const getSeverityRubricItems = async (
  annualReportId: string,
  request: GetRubricItemsRequest
): Promise<RubricItemBackend[]> => {
  try {
    return await apiRequest<RubricItemBackend[], GetRubricItemsRequest>(
      'POST',
      `/risk-assessment/rubric/severity/index-grid/${annualReportId}`,
      request,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to fetch severity rubric items',
    };
  }
};

/**
 * Update rubric item description
 * @param request - Rubric item update data
 * @returns Response from API
 */
export const updateRubricItem = async (
  request: UpdateRubricItemRequest
): Promise<UpdateRubricItemResponse> => {
  try {
    return await apiRequest<UpdateRubricItemResponse, UpdateRubricItemRequest>(
      'PUT',
      '/risk-assessment/rubric',
      request,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to update rubric item',
    };
  }
};

/**
 * Parse rubric items from backend format
 * Sorts by Number field
 */
export const parseRubricItems = (
  backendData: RubricItemBackend[]
): RubricItem[] => {
  if (!backendData || !Array.isArray(backendData)) {
    console.warn('⚠️ parseRubricItems received invalid data:', backendData);
    return [];
  }

  return backendData
    .map(item => ({
      id: item.Id,
      name: item.RubricItemTitle || '',
      description: item.RubricItemDescription || '',
      index: item.Number,
    }))
    .sort((a, b) => a.index - b.index);
};

// ========== FULL REPORT APIs (Consolidated across assessments) ==========

type FilterValue = string | boolean | number | null;

interface FilterCondition {
  field: string;
  operator: string;
  value: FilterValue;
  condition?: 'and' | 'or';
}

interface FilterPredicateGroup {
  predicates: FilterCondition[];
}

type WhereCondition = FilterCondition | FilterPredicateGroup;

const buildFullReportFilterConditions = () => {
  const whereConditions: WhereCondition[] = [];

  const addMultiValueFilter = (field: string, values: string[] | undefined) => {
    if (!values?.length) return;

    if (values.length === 1) {
      whereConditions.push({ field, operator: 'equal', value: values[0] });
    } else {
      whereConditions.push({
        predicates: values.map(value => ({
          field,
          operator: 'equal',
          value,
          condition: 'or' as const,
        })),
      });
    }
  };

  return { whereConditions, addMultiValueFilter };
};

export interface GetFullReportRequest {
  skip: number;
  take: number;
  requiresCounts: boolean;
  processIds?: string[];
}

export const getObjectivesFullReport = async (
  annualReportId: string,
  request: GetFullReportRequest
): Promise<GetObjectivesResponse> => {
  try {
    const { whereConditions, addMultiValueFilter } = buildFullReportFilterConditions();
    addMultiValueFilter('ProcessId', request.processIds);

    const requestBody: {
      skip: number;
      take: number;
      requiresCounts: boolean;
      where?: WhereCondition[];
    } = {
      skip: request.skip || 0,
      take: request.take || 1000,
      requiresCounts: request.requiresCounts,
    };

    if (whereConditions.length > 0) {
      requestBody.where = whereConditions;
    }

    return await apiRequest<GetObjectivesResponse, typeof requestBody>(
      'POST',
      `/risk-assessment/objectives/full-report/index-grid/${annualReportId}`,
      requestBody,
      true
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to fetch objectives full report',
    };
  }
};

export const getCompliancePurposesFullReport = async (
  annualReportId: string,
  request: GetFullReportRequest
): Promise<GetCompliancePurposesResponse> => {
  try {
    const { whereConditions, addMultiValueFilter } = buildFullReportFilterConditions();
    addMultiValueFilter('ProcessId', request.processIds);

    const requestBody: {
      skip: number;
      take: number;
      requiresCounts: boolean;
      where?: WhereCondition[];
    } = {
      skip: request.skip || 0,
      take: request.take || 1000,
      requiresCounts: request.requiresCounts,
    };

    if (whereConditions.length > 0) {
      requestBody.where = whereConditions;
    }

    return await apiRequest<GetCompliancePurposesResponse, typeof requestBody>(
      'POST',
      `/risk-assessment/compliance/full-report/index-grid/${annualReportId}`,
      requestBody,
      true
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to fetch compliance purposes full report',
    };
  }
};

